import { deleteReapproCategory, useReapproCategories } from '@gozoki/api'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog'
import { useRights } from '../../../utils/hooks'
import { ReapproCategoriesFormulas } from './ReapproCategoriesFormulas'
import { ReapproCategoriesTable } from './ReapproCategoriesTable'
import { ReapproCategoriesToolbar } from './ReapproCategoriesToolbar'
import { ReapproCategoryDialog } from './ReapproCategoryDialog'
import { ReapproNeedBulkImportDialog } from './ReapproNeedBulkImportDialog'
import { ReapproSimulationEmailDialog } from './ReapproSimulationEmailDialog'
import { ReapproStockMinBulkDialog } from './ReapproStockMinBulkDialog'

export const ReapproCategoriesPanel = () => {
  const isUserAdmin = useRights(['ADMIN'])
  const navigate = useNavigate()

  const [selectedCategories, setSelectedCategories] = useState<number[]>([])
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [simulationDialogOpen, setSimulationDialogOpen] = useState(false)
  const [stockMinDialogOpen, setStockMinDialogOpen] = useState(false)
  const [reapproImportDialogOpen, setReapproImportDialogOpen] = useState(false)

  const { data: reapproCategories = [], refetch, isLoading, isPending } = useReapproCategories()

  const deleteCategoryMut = useMutation({ mutationFn: deleteReapproCategory })

  const handleDeleteCategory = async () => {
    try {
      await deleteCategoryMut.mutateAsync(selectedCategories)
      setSelectedCategories([])
      refetch()
    } catch (error) {
      let message = 'Une erreur inconnue est survenue lors de la suppression de la catégorie'
      if (
        error instanceof AxiosError &&
        error.response?.data &&
        typeof error.response.data === 'object' &&
        'message' in error.response.data
      ) {
        message = error.response.data.message as string
      }
      toast.error(message)
    } finally {
      setDeleteConfirmOpen(false)
    }
  }

  return (
    <>
      <ReapproCategoriesToolbar
        isUserAdmin={isUserAdmin}
        onConfigure={() => navigate({ to: '/categories/reappro/config' })}
        onStocksMin={() => setStockMinDialogOpen(true)}
        onImport={() => setReapproImportDialogOpen(true)}
        onSimulation={() => setSimulationDialogOpen(true)}
      />

      <ReapproCategoriesTable
        categories={reapproCategories}
        isLoading={isLoading || isPending}
        selectedIds={selectedCategories}
        onSelectionChange={setSelectedCategories}
        isUserAdmin={isUserAdmin}
        isDeleting={deleteCategoryMut.isPending}
        onAdd={() => setAddDialogOpen(true)}
        onEdit={() => setEditDialogOpen(true)}
        onDelete={() => setDeleteConfirmOpen(true)}
      />

      <ReapproCategoriesFormulas />

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Êtes-vous sûr de vouloir supprimer{' '}
            {selectedCategories.length === 1 ? 'cette catégorie' : 'ces catégories'} ?
          </p>
          <DialogFooter>
            <DialogClose render={<Button variant="outline" />}>Annuler</DialogClose>
            <Button
              variant="destructive"
              onClick={handleDeleteCategory}
              disabled={deleteCategoryMut.isPending}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReapproStockMinBulkDialog open={stockMinDialogOpen} onOpenChange={setStockMinDialogOpen} />

      <ReapproNeedBulkImportDialog
        open={reapproImportDialogOpen}
        onOpenChange={setReapproImportDialogOpen}
      />

      <ReapproSimulationEmailDialog
        open={simulationDialogOpen}
        onOpenChange={setSimulationDialogOpen}
      />

      <ReapproCategoryDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        reapproCategoryId={editDialogOpen ? selectedCategories[0] : undefined}
        onSuccess={() => {
          refetch()
          setSelectedCategories([])
        }}
      />

      <ReapproCategoryDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSuccess={refetch}
      />
    </>
  )
}
